import { useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Modal } from "@/components/base/Modal";
import { parseCsv } from "@/lib/csv";
import * as inventoryApi from "@/lib/api/inventory";

interface ProductOption { id: string; name: string; sku: string; }
interface VariantOption { id: string; product_id: string; sku: string | null; }
interface LocationOption { id: string; name: string; code: string | null; }

interface ResolvedLine {
  product_id: string;
  variant_id: string | null;
  location_id: string;
  qty: number;
  unit_cost: number | null;
}

interface PreviewRow {
  line: ResolvedLine;
  sku: string;
  variantSku: string;
  location: string;
  qty: number;
  error: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  orgId: string;
  products: ProductOption[];
  variants: VariantOption[];
  locations: LocationOption[];
  onDone: () => void;
}

export default function OpeningInventoryModal({
  open, onClose, orgId, products, variants, locations, onDone,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<PreviewRow[]>([]);
  const [error, setError] = useState("");
  const [result, setResult] = useState("");
  const [saving, setSaving] = useState(false);
  const [hasExisting, setHasExisting] = useState(false);
  const [confirmExisting, setConfirmExisting] = useState(false);
  const [idemKey, setIdemKey] = useState("");

  useEffect(() => {
    if (!open) return;
    setPreview([]);
    setError("");
    setResult("");
    setConfirmExisting(false);
    setIdemKey(crypto.randomUUID());
    supabase
      .from("inventory_ledger")
      .select("id")
      .eq("org_id", orgId)
      .eq("entry_type", "opening_balance")
      .limit(1)
      .then(({ data }) => setHasExisting((data?.length ?? 0) > 0));
  }, [open, orgId]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setResult("");
    const text = await file.text();
    const rows = parseCsv(text);
    if (rows.length < 2) {
      setError("No data rows found in the CSV file.");
      e.target.value = "";
      return;
    }
    const headers = rows[0].map((h) => h.trim().toLowerCase());
    const idx = (n: string) => headers.indexOf(n);
    const skuIdx = idx("sku");
    const locIdx = idx("location");
    const qtyIdx = idx("quantity");
    const costIdx = idx("unit_cost");
    const vskuIdx = idx("variant_sku");

    if (skuIdx < 0 || locIdx < 0 || qtyIdx < 0) {
      setError("The CSV must include 'sku', 'location' and 'quantity' columns.");
      e.target.value = "";
      return;
    }

    const productBySku = new Map<string, ProductOption>();
    products.forEach((p) => { if (p.sku) productBySku.set(p.sku.trim().toLowerCase(), p); });
    const variantByKey = new Map<string, VariantOption>();
    variants.forEach((v) => { if (v.sku) variantByKey.set(`${v.product_id}::${v.sku.trim().toLowerCase()}`, v); });
    const locByName = new Map<string, LocationOption>();
    locations.forEach((l) => {
      locByName.set(l.name.trim().toLowerCase(), l);
      if (l.code) locByName.set(l.code.trim().toLowerCase(), l);
    });

    const resolved: PreviewRow[] = [];
    for (let i = 1; i < rows.length; i++) {
      const r = rows[i];
      const skuRaw = (r[skuIdx] ?? "").trim();
      const locRaw = (r[locIdx] ?? "").trim();
      const qtyRaw = (r[qtyIdx] ?? "").trim();
      const costRaw = costIdx >= 0 ? (r[costIdx] ?? "").trim() : "";
      const vskuRaw = vskuIdx >= 0 ? (r[vskuIdx] ?? "").trim() : "";

      const row: PreviewRow = {
        line: { product_id: "", variant_id: null, location_id: "", qty: 0, unit_cost: null },
        sku: skuRaw,
        variantSku: vskuRaw,
        location: locRaw,
        qty: Number(qtyRaw) || 0,
        error: null,
      };

      const product = productBySku.get(skuRaw.toLowerCase());
      const loc = locByName.get(locRaw.toLowerCase());
      const qty = Number(qtyRaw);

      const problems: string[] = [];
      if (!product) problems.push("Unknown SKU");
      if (!loc) problems.push("Unknown location");
      if (!qtyRaw || Number.isNaN(qty) || qty <= 0) problems.push("Invalid quantity");
      if (costRaw && Number.isNaN(Number(costRaw))) problems.push("Invalid unit cost");

      let variantId: string | null = null;
      if (product) {
        const productVariants = variants.filter((v) => v.product_id === product.id);
        if (productVariants.length > 0) {
          if (vskuRaw) {
            const v = variantByKey.get(`${product.id}::${vskuRaw.toLowerCase()}`);
            if (!v) problems.push("Unknown variant SKU");
            else variantId = v.id;
          } else {
            problems.push("Variant required");
          }
        }
      }

      if (problems.length === 0 && product && loc) {
        row.line = {
          product_id: product.id,
          variant_id: variantId,
          location_id: loc.id,
          qty,
          unit_cost: costRaw ? Number(costRaw) : null,
        };
      } else {
        row.error = problems.join("; ");
      }

      resolved.push(row);
    }

    setPreview(resolved);
    e.target.value = "";
  };

  const handleImport = async () => {
    const valid = preview.filter((r) => !r.error);
    if (valid.length === 0) {
      setError("No valid lines to import.");
      return;
    }
    setSaving(true);
    let res: inventoryApi.OpeningInventoryResult;
    try {
      res = await inventoryApi.importOpeningInventory(
        orgId,
        valid.map((r) => ({
          product_id: r.line.product_id,
          variant_id: r.line.variant_id,
          location_id: r.line.location_id,
          qty: r.line.qty,
          unit_cost: r.line.unit_cost,
        })),
        confirmExisting,
        idemKey,
      );
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }
    setSaving(false);
    setResult(`Imported ${res.imported} lines${res.failed ? `, ${res.failed} failed` : ""}.`);
    if (res.imported > 0) onDone();
  };

  const errorCount = preview.filter((r) => r.error).length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Import opening stock"
      wide
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={saving || preview.length === 0 || errorCount > 0 || (hasExisting && !confirmExisting)}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
          >
            {saving ? "Importing..." : "Import stock"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <p className="text-sm text-slate-600 leading-relaxed">
            Upload existing stock so you don't have to create fake purchase orders. Opening stock is recorded as a trusted inventory movement with a clear audit trail.
          </p>
          <p className="text-xs text-slate-400 mt-2">
            Expected CSV columns: <span className="font-mono">sku, location, quantity, unit_cost, variant_sku</span> (the last two are optional).
          </p>
        </div>

        {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
        {result && <div className="px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">{result}</div>}

        <div>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-upload-2-line" /> Choose CSV file
          </button>
        </div>

        {preview.length > 0 && (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 text-xs font-medium text-slate-500 flex items-center justify-between">
              <span>{preview.length} rows</span>
              <span className={errorCount > 0 ? "text-red-600" : "text-emerald-600"}>{errorCount} with errors</span>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-50">
              {preview.map((r, i) => (
                <div key={i} className="px-3 py-2 flex items-center gap-3 text-sm">
                  <span className="font-mono text-slate-700 w-32 truncate">{r.sku}</span>
                  {r.variantSku && <span className="text-xs text-slate-400 font-mono">{r.variantSku}</span>}
                  <span className="text-slate-500 w-32 truncate">{r.location}</span>
                  <span className="text-slate-700 font-medium w-12 text-right">{r.qty}</span>
                  {r.error ? (
                    <span className="text-xs text-red-600 flex-1">{r.error}</span>
                  ) : (
                    <span className="text-xs text-emerald-600 flex-1">Ready</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {hasExisting && (
          <label className="flex items-start gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={confirmExisting}
              onChange={(e) => setConfirmExisting(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
            />
            <span>Opening stock already exists for this organisation. I understand importing again will <strong>add</strong> to current stock.</span>
          </label>
        )}
      </div>
    </Modal>
  );
}